import { Button, Heading, Label, Text, TextInput } from '@elladx/design-system';
import { useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { API_AUTH_REQUIRED_EVENT, API_CONFIG_CHANGED_EVENT, getDefaultApiUrl, getRuntimeApiConfig, getStoredApiUrl, isAllowedApiUrl, saveRuntimeApiConfig, } from '../api/config';
import { BILLABLE_LOCK_EVENT } from '../hooks/useBillableNavigationLock';
import { SessionActionProvider } from './SessionActionContext';
import AuthShell from './AuthShell';
/** Strip the scheme so the connected API host reads cleanly in the sidebar. */
function hostLabel(url: string): string {
    return url.replace(/^https?:\/\//, '').replace(/\/+$/, '');
}
interface TokenGateProps {
    children: ReactNode;
}
/**
 * Legacy runtime shared-token gate. Used when Cognito SSO is not configured.
 */
function TokenGate({ children }: TokenGateProps) {
    const initialConfig = getRuntimeApiConfig();
    const [configured, setConfigured] = useState(Boolean(initialConfig));
    const [editing, setEditing] = useState(false);
    const [apiUrl, setApiUrl] = useState(initialConfig?.apiUrl ?? getDefaultApiUrl());
    const [token, setToken] = useState('');
    const [validationError, setValidationError] = useState('');
    const [billableLocked, setBillableLocked] = useState(false);
    useEffect(() => {
        const handleAuthRequired = () => {
            const current = getRuntimeApiConfig();
            setConfigured(false);
            setEditing(false);
            setApiUrl(current?.apiUrl ?? getStoredApiUrl());
            setToken('');
            setValidationError('Your shipping connection has expired. Enter a valid access token.');
        };
        const handleConfigChanged = () => setConfigured(Boolean(getRuntimeApiConfig()));
        const handleBillableLock = (event: Event) => {
            setBillableLocked(Boolean((event as CustomEvent<boolean>).detail));
        };
        window.addEventListener(API_AUTH_REQUIRED_EVENT, handleAuthRequired);
        window.addEventListener(API_CONFIG_CHANGED_EVENT, handleConfigChanged);
        window.addEventListener(BILLABLE_LOCK_EVENT, handleBillableLock);
        return () => {
            window.removeEventListener(API_AUTH_REQUIRED_EVENT, handleAuthRequired);
            window.removeEventListener(API_CONFIG_CHANGED_EVENT, handleConfigChanged);
            window.removeEventListener(BILLABLE_LOCK_EVENT, handleBillableLock);
        };
    }, []);
    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const normalizedUrl = apiUrl.trim().replace(/\/+$/, '');
        const normalizedToken = token.trim();
        if (!normalizedToken) {
            setValidationError('An access token is required.');
            return;
        }
        if (!isAllowedApiUrl(normalizedUrl)) {
            setValidationError(import.meta.env.DEV
                ? 'Enter a valid HTTP or HTTPS shipping API URL, or leave it blank for the local connection.'
                : 'Enter a valid HTTPS shipping API URL.');
            return;
        }
        try {
            saveRuntimeApiConfig({ apiUrl: normalizedUrl, token: normalizedToken });
        }
        catch {
            setValidationError('Session storage is unavailable. Enable it for this site and try again.');
            return;
        }
        setConfigured(true);
        setEditing(false);
        setToken('');
        setValidationError('');
    };
    if (!configured || editing) {
        return (<AuthShell>
          <Heading as="h1" variant="display">Connect to shipping</Heading>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <Label htmlFor="runtime-api-url">Shipping API URL *</Label>
              <TextInput id="runtime-api-url" type="url" value={apiUrl} onChange={(event) => setApiUrl(event.target.value)} placeholder={import.meta.env.DEV ? 'Local shipping connection' : 'https://api.example.com'} required={!import.meta.env.DEV} autoComplete="url"/>
              {import.meta.env.DEV && (<Text className="field-hint">Leave blank to use your local shipping connection.</Text>)}
            </div>

            <div className="form-group">
              <Label htmlFor="runtime-api-token">Access token *</Label>
              <TextInput id="runtime-api-token" type="password" value={token} onChange={(event) => setToken(event.target.value)} required autoComplete="off"/>
            </div>

            {validationError && (<Text role="alert" className="auth-error">
                {validationError}
              </Text>)}

            <div className="action-buttons">
              <Button type="submit" variant="primary">
                Connect
              </Button>
              {configured && (<Button type="button" onClick={() => {
                    setEditing(false);
                    setToken('');
                    setValidationError('');
                }} variant="ghost">
                  Cancel
                </Button>)}
            </div>
          </form>
      </AuthShell>);
    }
    const connectedUrl = getRuntimeApiConfig()?.apiUrl ?? '';
    return (<SessionActionProvider value={{
            onAction: () => {
                const current = getRuntimeApiConfig();
                setApiUrl(current?.apiUrl ?? getDefaultApiUrl());
                // Never copy an existing bearer token back into an editable field.
                // Re-authentication requires deliberate re-entry.
                setToken('');
                setEditing(true);
            },
            actionLabel: 'Edit shipping connection',
            actionTitle: billableLocked
                ? 'Connection settings are locked while a billable request is processing.'
                : 'Edit shipping connection',
            disabled: billableLocked,
            userName: 'Shipping connection',
            userEmail: connectedUrl ? hostLabel(connectedUrl) : undefined,
        }}>
      {children}
    </SessionActionProvider>);
}
export default TokenGate;
